import axios from "axios";
import authHeader from "./auth-header";
import authService from "./auth.service";

// URL base de la API del módulo de rutas
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

// Configurar interceptor de axios para manejar errores de autenticación
axios.interceptors.response.use(
  (response) => {
    // Si la respuesta es exitosa, simplemente retornarla
    return response;
  },
  (error) => {
    // Si hay un error de respuesta
    if (error.response) {
      // Token expirado o no válido (401 Unauthorized)
      if (error.response.status === 401) {
        console.warn("⚠️ Token expirado o no válido. Redirigiendo al login...");

        // Limpiar datos de autenticación
        authService.logout();

        // Redirigir al login
        window.location.href = "/login";

        // Retornar un error personalizado
        return Promise.reject({
          ...error,
          message: "Sesión expirada. Por favor, inicie sesión nuevamente.",
        });
      }

      // Forbidden (403) - Usuario no tiene permisos
      if (error.response.status === 403) {
        console.warn("⚠️ Acceso denegado");
        return Promise.reject({
          ...error,
          message: "No tiene permisos para realizar esta acción.",
        });
      }
    }

    // Para otros errores, retornar el error original
    return Promise.reject(error);
  },
);

/**
 * Servicio para consumir la API existente de vegetales
 * Se conecta a las tablas ver_purchases y ver_addresess
 */
class ApiService {
  /**
   * Obtener pedidos del día con coordenadas GPS
   * @param {string} date - Fecha en formato YYYY-MM-DD
   * @param {string} token - Token JWT de autenticación
   * @returns {Promise<Array>} Pedidos con coordenadas
   */
  async getPurchasesByDate(date) {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/delivery/orders`, {
        params: {
          date: date,
        },
        headers: authHeader(),
      });

      // La respuesta del backend ya viene con la estructura correcta
      // response.data contiene: { all, byTimeWindow, byDistrict, stats }
      const orders = response.data.all || [];

      // Transformar datos al formato esperado por el frontend
      return orders.map((order) => ({
        id: order.id,
        customer_name: order.customer_name || "Cliente",
        district: order.district,
        address: order.address,
        full_address: order.full_address,
        type_address: order.type_address || null,
        number: order.number || "",
        interior: order.interior || "",
        url_google: order.url_google || "",
        date_programmed: order.date_programmed,
        hour_programmed_start: order.time_programmed,
        hour_programmed_end: order.hour_programmed_end,
        delivery: 0, // No disponible en la nueva estructura
        total: parseFloat(order.total || 0),
        status_delivery: order.status,
        latitude: order.latitude || null,
        longitude: order.longitude || null,
        coordinates_updated_at: order.coordinates_updated_at || null,
        id_user: null,
        id_address: order.id_address,
        subtotal: parseFloat(order.total || 0),
        discount: 0,
        customer_phone: order.customer_phone || "",
        leave_your_order: order.leave_your_order || null,
        coord_update_source: order.coord_update_source || 'ORIGINAL',
        coords_validated: order.coords_validated || 'NOT_VALIDATED',
      }));
    } catch (error) {
      console.error("Error fetching purchases:", error);
      throw error;
    }
  }

  /**
   * Agrupar pedidos por ventana horaria
   * @param {Array} purchases - Array de pedidos
   * @returns {Object} Pedidos agrupados por ventana horaria
   */
  groupByTimeWindow(purchases) {
    const grouped = {};

    purchases.forEach((purchase) => {
      const timeSlot = `${purchase.hour_programmed_start}-${purchase.hour_programmed_end}`;

      if (!grouped[timeSlot]) {
        grouped[timeSlot] = {
          timeSlot,
          timeSlotStart: purchase.hour_programmed_start,
          timeSlotEnd: purchase.hour_programmed_end,
          orderCount: 0,
          orders: [],
          totalAmount: 0,
          withCoordinates: 0,
        };
      }

      grouped[timeSlot].orders.push(purchase);
      grouped[timeSlot].orderCount++;
      grouped[timeSlot].totalAmount += purchase.total;
      if (purchase.latitude && purchase.longitude) {
        grouped[timeSlot].withCoordinates++;
      }
    });

    return grouped;
  }

  /**
   * Agrupar pedidos por distrito
   * @param {Array} purchases - Array de pedidos
   * @returns {Object} Pedidos agrupados por distrito
   */
  groupByDistrict(purchases) {
    const grouped = {};

    purchases.forEach((purchase) => {
      const district = purchase.district || "Sin distrito";

      if (!grouped[district]) {
        grouped[district] = {
          district,
          orderCount: 0,
          orders: [],
          totalAmount: 0,
          totalDelivery: 0,
          withCoordinates: 0,
          centroid: null,
        };
      }

      grouped[district].orders.push(purchase);
      grouped[district].orderCount++;
      grouped[district].totalAmount += purchase.total;
      grouped[district].totalDelivery += purchase.delivery;

      if (purchase.latitude && purchase.longitude) {
        grouped[district].withCoordinates++;
      }
    });

    // Calcular centroides para distritos con coordenadas
    Object.keys(grouped).forEach((district) => {
      const ordersWithCoords = grouped[district].orders.filter(
        (o) => o.latitude && o.longitude,
      );

      if (ordersWithCoords.length > 0) {
        const sumLat = ordersWithCoords.reduce((sum, o) => sum + o.latitude, 0);
        const sumLng = ordersWithCoords.reduce(
          (sum, o) => sum + o.longitude,
          0,
        );

        grouped[district].centroid = {
          lat: sumLat / ordersWithCoords.length,
          lng: sumLng / ordersWithCoords.length,
        };
      }
    });

    return grouped;
  }

  /**
   * Crear clusters geográficos automáticos
   * Solo agrupa pedidos que tienen coordenadas GPS
   * @param {Array} purchases - Array de pedidos
   * @param {number} maxDistance - Distancia máxima en km para agrupar (default: 3km)
   * @returns {Array} Clusters con pedidos cercanos
   */
  createGeoClusters(purchases, maxDistance = 3) {
    // Filtrar solo pedidos con coordenadas
    const ordersWithCoords = purchases.filter((p) => p.latitude && p.longitude);

    if (ordersWithCoords.length === 0) {
      return [];
    }

    const clusters = [];
    const processed = new Set();

    ordersWithCoords.forEach((order, index) => {
      if (processed.has(index)) return;

      const cluster = {
        clusterId: clusters.length + 1,
        orders: [order],
        orderCount: 1,
        centroid: { lat: order.latitude, lng: order.longitude },
        avgDelivery: order.delivery,
        totalAmount: order.total,
        districts: new Set([order.district]),
      };

      processed.add(index);

      // Buscar pedidos cercanos
      ordersWithCoords.forEach((otherOrder, otherIndex) => {
        if (processed.has(otherIndex) || index === otherIndex) return;

        const distance = this.haversineDistance(
          order.latitude,
          order.longitude,
          otherOrder.latitude,
          otherOrder.longitude,
        );

        if (distance <= maxDistance) {
          cluster.orders.push(otherOrder);
          cluster.orderCount++;
          cluster.totalAmount += otherOrder.total;
          cluster.districts.add(otherOrder.district);
          processed.add(otherIndex);
        }
      });

      // Recalcular centroide del cluster
      const sumLat = cluster.orders.reduce((sum, o) => sum + o.latitude, 0);
      const sumLng = cluster.orders.reduce((sum, o) => sum + o.longitude, 0);
      cluster.centroid = {
        lat: sumLat / cluster.orders.length,
        lng: sumLng / cluster.orders.length,
      };

      cluster.avgDelivery =
        cluster.orders.reduce((sum, o) => sum + o.delivery, 0) /
        cluster.orders.length;
      cluster.districts = Array.from(cluster.districts);

      clusters.push(cluster);
    });

    return clusters;
  }

  /**
   * Calcular distancia entre dos puntos GPS usando fórmula de Haversine
   * @param {number} lat1 - Latitud punto 1
   * @param {number} lng1 - Longitud punto 1
   * @param {number} lat2 - Latitud punto 2
   * @param {number} lng2 - Longitud punto 2
   * @returns {number} Distancia en kilómetros
   */
  haversineDistance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Radio de la Tierra en km
    const dLat = this.toRadians(lat2 - lat1);
    const dLng = this.toRadians(lng2 - lng1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  /**
   * Optimizar ruta usando algoritmo Nearest Neighbor
   * @param {Array} orders - Pedidos a optimizar (deben tener lat/lng)
   * @param {Object} startPoint - Punto de inicio {lat, lng}
   * @returns {Array} Ruta optimizada
   */
  optimizeRouteNearestNeighbor(
    orders,
    startPoint = { lat: -12.0464, lng: -77.0428 },
  ) {
    if (!orders || orders.length === 0) return [];

    // Filtrar solo pedidos con coordenadas
    const ordersWithCoords = orders.filter((o) => o.latitude && o.longitude);

    if (ordersWithCoords.length === 0) return [];

    const route = [];
    const unvisited = [...ordersWithCoords];
    let current = startPoint;
    let totalDistance = 0;

    while (unvisited.length > 0) {
      let nearest = null;
      let minDist = Infinity;
      let nearestIdx = -1;

      unvisited.forEach((order, idx) => {
        const dist = this.haversineDistance(
          current.lat,
          current.lng,
          order.latitude,
          order.longitude,
        );

        if (dist < minDist) {
          minDist = dist;
          nearest = order;
          nearestIdx = idx;
        }
      });

      totalDistance += minDist;

      route.push({
        stopNumber: route.length + 1,
        orderId: nearest.id,
        customer: nearest.customer_name,
        address: `${nearest.address} ${nearest.number}${nearest.interior ? " " + nearest.interior : ""}, ${nearest.district}`,
        coordinates: {
          lat: nearest.latitude,
          lng: nearest.longitude,
        },
        distanceFromPrevious: minDist,
        travelTime: this.calculateTravelTime(minDist),
        timeWindow: {
          start: nearest.hour_programmed_start,
          end: nearest.hour_programmed_end,
        },
        delivery: nearest.delivery,
        total: nearest.total,
      });

      unvisited.splice(nearestIdx, 1);
      current = { lat: nearest.latitude, lng: nearest.longitude };
    }

    return {
      route,
      totalDistance,
      totalStops: route.length,
      estimatedDuration: route.reduce(
        (sum, stop) => sum + stop.travelTime + 10,
        0,
      ), // +10 min por parada
    };
  }

  /**
   * Calcular tiempo de viaje estimado
   * @param {number} distanceKm - Distancia en kilómetros
   * @param {number} avgSpeed - Velocidad promedio en km/h (default: 25)
   * @returns {number} Tiempo en minutos
   */
  calculateTravelTime(distanceKm, avgSpeed = 25) {
    const timeHours = distanceKm / avgSpeed;
    return Math.ceil(timeHours * 60);
  }

  /**
   * Optimizar ruta en el backend y guardar en BD
   * @param {string} date - Fecha de la ruta
   * @param {Array} purchaseIds - IDs de los pedidos a incluir
   * @param {Object} options - Opciones adicionales (routeName, startTime, etc)
   * @returns {Promise<Object>} Ruta optimizada guardada
   */
  async optimizeAndSaveRoute(date, purchaseIds, options = {}) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/delivery/routes/optimize`,
        {
          date,
          purchaseIds,
          options,
        },
        {
          headers: authHeader(),
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error optimizing route:", error);
      throw error;
    }
  }

  /**
   * Obtener rutas guardadas
   * @param {string} date - Fecha opcional para filtrar
   * @param {string} status - Estado opcional para filtrar
   * @returns {Promise<Array>} Lista de rutas
   */
  async getSavedRoutes(date = null, status = null) {
    try {
      const params = {};
      if (date) params.date = date;
      if (status) params.status = status;

      const response = await axios.get(`${API_BASE_URL}/api/delivery/routes`, {
        params,
        headers: authHeader(),
      });

      console.log("📍 API Response for getSavedRoutes:", response.data);
      if (response.data && response.data.length > 0) {
        console.log("📍 First route:", response.data[0]);
        console.log("📍 First route stops:", response.data[0].stops);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching routes:", error);
      throw error;
    }
  }

  /**
   * Obtener una ruta específica por ID
   * @param {number} routeId - ID de la ruta
   * @returns {Promise<Object>} Ruta con todas sus paradas
   */
  async getRouteById(routeId) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/delivery/routes/${routeId}`,
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching route:", error);
      throw error;
    }
  }

  /**
   * Actualizar estado de una ruta
   * @param {number} routeId - ID de la ruta
   * @param {string} status - Nuevo estado (pending, in_progress, completed, cancelled)
   * @returns {Promise<Object>} Ruta actualizada
   */
  async updateRouteStatus(routeId, status) {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/delivery/routes/${routeId}/status`,
        {
          status,
        },
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating route status:", error);
      throw error;
    }
  }

  /**
   * Actualizar estado de una parada específica
   * @param {number} stopId - ID de la parada
   * @param {string} status - Nuevo estado (pending, in_transit, completed, failed)
   * @returns {Promise<Object>} Parada actualizada
   */
  async updateStopStatus(stopId, status) {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/delivery/stops/${stopId}/status`,
        {
          status,
        },
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating stop status:", error);
      throw error;
    }
  }

  /**
   * Validar y corregir automáticamente las coordenadas de los pedidos de una fecha
   * usando Google Places Geocoding API.
   * @param {string} date - Fecha en formato YYYY-MM-DD
   * @returns {Promise<Object>} { results: { total, updated, confirmed, failed, changes } }
   */
  async validateAddressesForDate(date) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/delivery/addresses/validate-date`,
        { date },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error validando direcciones con Places:', error);
      throw error;
    }
  }

  /**
   * Google Places Autocomplete (proxy via backend)
   * @param {string} query - texto a buscar
   * @returns {Promise<Array>} [{ place_id, description }]
   */
  async placesAutocomplete(query) {
    const response = await axios.get(`${API_BASE_URL}/api/delivery/addresses/places-autocomplete`, {
      params: { query },
      headers: authHeader(),
    });
    return response.data.suggestions || [];
  }

  /**
   * Google Place Details (proxy via backend)
   * @param {string} placeId - place_id de Google
   * @returns {Promise<Object>} { latitude, longitude, formatted_address, url_google }
   */
  async placeDetails(placeId) {
    const response = await axios.get(`${API_BASE_URL}/api/delivery/addresses/place-details`, {
      params: { place_id: placeId },
      headers: authHeader(),
    });
    return response.data;
  }

  /**
   * Reverse geocoding: lat/lng → formatted_address (proxy via backend)
   */
  async reverseGeocode(lat, lng) {
    const response = await axios.get(`${API_BASE_URL}/api/delivery/addresses/reverse-geocode`, {
      params: { lat, lng },
      headers: authHeader(),
    });
    return response.data.formatted_address || null;
  }

  /**
   * Actualizar coordenadas de una dirección
   * @param {number} addressId - ID de la dirección
   * @param {Object} data - { latitude, longitude, url_google }
   * @returns {Promise<Object>} Dirección actualizada
   */
  async updateAddressCoordinates(addressId, data) {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/delivery/addresses/${addressId}/coordinates`,
        data,
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating address coordinates:", error);
      throw error;
    }
  }

  /**
   * Obtener direcciones sin coordenadas de los últimos 180 días
   * @returns {Promise<Array>} Lista de direcciones sin coordenadas
   */
  async getMissingCoordinates() {
    try {
      var response = await axios.get(
        API_BASE_URL + "/api/delivery/missing-coordinates",
        {
          headers: authHeader(),
        },
      );
      return response.data.addresses;
    } catch (error) {
      console.error("Error fetching missing coordinates:", error);
      throw error;
    }
  }

  async markAddressValidated(addressId, coords_validated) {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/delivery/addresses/${addressId}/coords-validated`,
        { coords_validated },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error marking address validated:', error);
      throw error;
    }
  }

  async validateMissingCoordinates() {
    try {
      var response = await axios.post(
        API_BASE_URL + "/api/delivery/missing-coordinates/validate",
        {},
        { headers: authHeader() }
      );
      return response.data;
    } catch (error) {
      console.error("Error validating missing coordinates:", error);
      throw error;
    }
  }

  // =====================================================
  // CONFIGURACIÓN
  // =====================================================

  async getConfig() {
    try {
      var response = await axios.get(
        API_BASE_URL + '/api/delivery/config',
        { headers: authHeader() }
      );
      return response.data.configs;
    } catch (error) {
      console.error('Error fetching config:', error);
      throw error;
    }
  }

  async updateConfig(key, value) {
    try {
      var response = await axios.put(
        API_BASE_URL + '/api/delivery/config/' + key,
        { value },
        { headers: authHeader() }
      );
      return response.data.config;
    } catch (error) {
      console.error('Error updating config:', error);
      throw error;
    }
  }

  // =====================================================
  // NUEVO-FLUJO: Cola Dinámica de Pedidos
  // =====================================================

  /**
   * Obtener estado de ventanas del día
   * @param {string} date - Fecha en formato YYYY-MM-DD
   * @returns {Promise<Object>} { windows: [...] }
   */
  async getWindowsStatus(date) {
    try {
      var response = await axios.get(
        API_BASE_URL + "/api/delivery/queue/windows/" + date,
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching windows status:", error);
      throw error;
    }
  }

  /**
   * Predice cómo quedarían agrupadas las rutas futuras (pedidos sin ruta generada aún),
   * usando el mismo algoritmo de producción. No escribe nada en BD.
   * @param {string} date - Fecha en formato YYYY-MM-DD
   * @returns {Promise<Object>} { predictedRoutes: [...] }
   */
  async getPredictedRoutes(date) {
    try {
      var response = await axios.get(
        API_BASE_URL + "/api/delivery/queue/predicted-routes/" + date,
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching predicted routes:", error);
      throw error;
    }
  }

  /**
   * Obtener cola de pedidos para una ventana específica
   * @param {string} date - Fecha en formato YYYY-MM-DD
   * @param {string} windowStart - Hora de inicio de ventana (HH:MM:SS)
   * @returns {Promise<Object>} { queue: [...], routeCapacity: {...} }
   */
  async getQueue(date, windowStart, windowEnd) {
    try {
      var params = windowEnd ? { windowEnd } : {};
      var response = await axios.get(
        API_BASE_URL + "/api/delivery/queue/" + date + "/" + windowStart,
        {
          headers: authHeader(),
          params,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching queue:", error);
      throw error;
    }
  }

  /**
   * Obtener pedidos que requieren llamada telefónica
   * @param {string} date - Fecha en formato YYYY-MM-DD
   * @returns {Promise<Object>} { orders: [...] }
   */
  async getRequiresCall(date) {
    try {
      var response = await axios.get(
        API_BASE_URL + "/api/delivery/queue/requires-call/" + date,
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching requires call orders:", error);
      throw error;
    }
  }

  /**
   * Agregar pedido a la cola manualmente
   * @param {Object} data - { purchaseId, windowDate, startTime, endTime }
   * @returns {Promise<Object>} Resultado de la operación
   */
  async addToQueue(data) {
    try {
      var response = await axios.post(
        API_BASE_URL + "/api/delivery/queue/add",
        data,
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error adding to queue:", error);
      throw error;
    }
  }

  /**
   * Recalcular cola de una ventana
   * @param {Object} data - { windowDate, startTime, endTime }
   * @returns {Promise<Object>} { queue: [...], routeCapacity: {...} }
   */
  async recalculateQueue(data) {
    try {
      var response = await axios.post(
        API_BASE_URL + "/api/delivery/queue/recalculate",
        data,
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error recalculating queue:", error);
      throw error;
    }
  }

  /**
   * Quitar pedido de la cola
   * @param {Object} data - { purchaseId, windowDate }
   */
  async removeFromQueue(data) {
    try {
      var response = await axios.post(
        API_BASE_URL + "/api/delivery/queue/remove",
        data,
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error removing from queue:", error);
      throw error;
    }
  }

  /**
   * Limpiar estado de notificación de overflow incorrecta de un pedido
   * @param {Object} data - { purchaseId, windowDate }
   */
  async clearOverflowNotification(data) {
    try {
      var response = await axios.post(
        API_BASE_URL + "/api/delivery/queue/clear-overflow",
        data,
        { headers: authHeader() }
      );
      return response.data;
    } catch (error) {
      console.error("Error clearing overflow notification:", error);
      throw error;
    }
  }

  async deferToNext(data) {
    try {
      var response = await axios.post(
        API_BASE_URL + "/api/delivery/queue/defer-to-next",
        data,
        { headers: authHeader() }
      );
      return response.data;
    } catch (error) {
      console.error("Error en defer-to-next:", error);
      throw error;
    }
  }

  async deferSilent(data) {
    try {
      var response = await axios.post(
        API_BASE_URL + "/api/delivery/queue/defer-silent",
        data,
        { headers: authHeader() }
      );
      return response.data;
    } catch (error) {
      console.error("Error en defer-silent:", error);
      throw error;
    }
  }

  async getRemovedAndDeferred(date) {
    try {
      var response = await axios.get(
        API_BASE_URL + "/api/delivery/queue/removed-deferred/" + date,
        { headers: authHeader() }
      );
      return response.data;
    } catch (error) {
      console.error("Error en getRemovedAndDeferred:", error);
      throw error;
    }
  }

  async packLeft(data) {
    try {
      var response = await axios.post(
        API_BASE_URL + "/api/delivery/queue/pack-left",
        data,
        { headers: authHeader() }
      );
      return response.data;
    } catch (error) {
      console.error("Error en pack-left:", error);
      throw error;
    }
  }

  /**
   * Registrar respuesta manual del cliente a propuesta de cambio
   * @param {Object} data - { purchaseId, windowDate, response: 'ACCEPTED'|'REJECTED' }
   */
  async handleCustomerResponse(data) {
    try {
      var response = await axios.post(
        API_BASE_URL + "/api/delivery/queue/customer-response",
        data,
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error handling customer response:", error);
      throw error;
    }
  }

  /**
   * Proponer mover un pedido a la primera ruta del día
   * @param {Object} data - { purchaseId, windowDate }
   */
  async proposeFirstRoute(data) {
    try {
      var response = await axios.post(
        API_BASE_URL + "/api/delivery/queue/propose-first-route",
        {
          ...data,
          manual: true,
        },
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error proposing first route:", error);
      throw error;
    }
  }

  /**
   * Confirmar movimiento a primera ruta (admin confirma manualmente)
   * @param {Object} data - { purchaseId, windowDate }
   */
  async confirmFirstRoute(data) {
    try {
      var response = await axios.post(
        API_BASE_URL + "/api/delivery/queue/confirm-first-route",
        data,
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error confirming first route:", error);
      throw error;
    }
  }

  /**
   * Marcar pedido como no entregado (envía WhatsApp al cliente)
   * @param {Object} data - { purchaseId, windowDate, reason }
   */
  async markNotDelivered(data) {
    try {
      var response = await axios.post(
        API_BASE_URL + "/api/delivery/queue/not-delivered",
        data,
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error marking as not delivered:", error);
      throw error;
    }
  }

  /**
   * Actualizar estado de entrega de un pedido en cola
   * @param {Object} data - { purchaseId, windowDate, status: 'PENDING'|'IN_ROUTE'|'DELIVERED'|'NOT_DELIVERED' }
   */
  async updateDeliveryStatus(data) {
    try {
      var response = await axios.patch(
        API_BASE_URL + "/api/delivery/queue/delivery-status",
        data,
        {
          headers: authHeader(),
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating delivery status:", error);
      throw error;
    }
  }

  // =====================================================
  // GESTIÓN DE USUARIOS
  // =====================================================

  async getUsers() {
    const response = await axios.get(`${API_BASE_URL}/api/users`, {
      headers: authHeader(),
    });
    return response.data.data;
  }

  async createUser(data) {
    const response = await axios.post(`${API_BASE_URL}/api/users`, data, {
      headers: authHeader(),
    });
    return response.data.data;
  }

  async updateUser(id, data) {
    const response = await axios.put(`${API_BASE_URL}/api/users/${id}`, data, {
      headers: authHeader(),
    });
    return response.data.data;
  }

  async deleteUser(id) {
    const response = await axios.delete(`${API_BASE_URL}/api/users/${id}`, {
      headers: authHeader(),
    });
    return response.data;
  }

  async resetUserPassword(id, password_nuevo) {
    const response = await axios.put(
      `${API_BASE_URL}/api/users/${id}/password`,
      { password_nuevo },
      { headers: authHeader() },
    );
    return response.data;
  }
}

export default new ApiService();
